import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowLeft, Buildings, FileArrowUp, HandCoins, Hash, ChatText,
    WarningOctagon, Clock, PaperPlaneTilt, ClockCounterClockwise,
    CurrencyInr, Receipt as ReceiptIcon, FileText as FileTextIcon, Bell, LockKey, Printer,
    ArrowsClockwise, Plus, Trash, Pencil,
    Prohibit, ArrowCounterClockwise, DotsThreeVertical, CalendarBlank,
    Database,
} from "@phosphor-icons/react";
import { api, formatApiError, previewDocument, downloadDocument } from "../lib/api";
import { formatINR, formatDate, formatDateTime } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import CorrectSettlementDialog from "../components/CorrectSettlementDialog";
import VoidPaymentDialog from "../components/VoidPaymentDialog";
import EditPaymentDateDialog from "../components/EditPaymentDateDialog";
import EditInvoiceDateDialog from "../components/EditInvoiceDateDialog";
import CancelInvoiceDialog from "../components/CancelInvoiceDialog";
import { CommercialSummary } from "../components/billing/CommercialSummary";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

/**
 * Iter 37: Render extra context + secure attachment download for invoices
 * recorded from external systems (Tally / SAP / Excel) via the AMC offline
 * record flow. Auth-only download — backend gates by super_admin/accounts.
 */
function OfflineInvoiceCard({ invoice }) {
    const [downloading, setDownloading] = useState(false);
    const att = invoice.attachment;

    const download = async () => {
        if (!att) return;
        setDownloading(true);
        try {
            const resp = await api.get(`/billing/amc/invoice/${invoice.id}/attachment`, {
                responseType: "blob",
            });
            const blob = new Blob([resp.data], { type: att.content_type || resp.headers["content-type"] || "application/octet-stream" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.original_filename || `${invoice.invoice_number || "attachment"}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to download attachment"));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="reedos-card border-amber-200 bg-amber-50/30" data-testid="offline-invoice-card">
            <div className="px-5 py-4 border-b border-amber-200 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="reedos-eyebrow text-amber-700">Offline invoice</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Recorded from an external system</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-xl">
                        This invoice was raised outside REEDOS BCMS (e.g. Tally / SAP / Excel) and recorded here so the AMC billing cycle is correctly tracked. Internal numbering is unaffected; the external reference is preserved verbatim.
                    </p>
                </div>
                {att && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={download}
                        disabled={downloading}
                        className="gap-1.5 shrink-0 border-amber-300 hover:bg-amber-100/60"
                        data-testid="offline-attachment-download-btn"
                    >
                        <FileArrowUp size={14} weight="duotone" className="rotate-180" />
                        {downloading ? "Downloading…" : "Download attachment"}
                    </Button>
                )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-amber-200/60">
                <div className="px-4 py-3">
                    <div className="reedos-eyebrow">External invoice #</div>
                    <div className="text-sm text-slate-900 mt-1 font-mono" data-testid="offline-external-ref">
                        {invoice.external_invoice_number || "—"}
                    </div>
                </div>
                <div className="px-4 py-3">
                    <div className="reedos-eyebrow">Service period</div>
                    <div className="text-sm text-slate-900 mt-1">
                        {invoice.billing_start_date && invoice.billing_end_date
                            ? <>{formatDate(invoice.billing_start_date)} → {formatDate(invoice.billing_end_date)}</>
                            : "—"}
                    </div>
                </div>
                <div className="px-4 py-3">
                    <div className="reedos-eyebrow">Linked AMC</div>
                    <div className="text-sm text-slate-900 mt-1">
                        {invoice.amc_schedule_id
                            ? <Link to={`/amc`} className="font-mono text-xs hover:underline">{invoice.amc_schedule_id}</Link>
                            : "—"}
                    </div>
                </div>
                <div className="px-4 py-3">
                    <div className="reedos-eyebrow">Attachment</div>
                    <div className="text-sm text-slate-900 mt-1 truncate" data-testid="offline-attachment-filename">
                        {att ? (
                            <span title={att.original_filename}>
                                {att.original_filename}
                                {typeof att.size_bytes === "number" && (
                                    <span className="text-slate-400 text-[11px] ml-1">({(att.size_bytes / 1024).toFixed(0)} KB)</span>
                                )}
                            </span>
                        ) : <span className="text-slate-400">None</span>}
                    </div>
                </div>
            </div>
        </div>
    );
}

function IssueCreditNoteDialog({ invoice, onIssued }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reasonCode, setReasonCode] = useState("price_revision");
    const [reasonNotes, setReasonNotes] = useState("");
    const [reasons, setReasons] = useState([]);
    const [lines, setLines] = useState([]);
    const baselineRef = useRef("");

    // Single snapshot string we compare against — covers reason + notes + lines
    const formSnapshot = useMemo(
        () => JSON.stringify({ reasonCode, reasonNotes, lines }),
        [reasonCode, reasonNotes, lines],
    );
    const isDirty = formSnapshot !== baselineRef.current;

    // Compute per-line max creditable = source amount - already credited
    const sourceById = useMemo(() => {
        const m = new Map();
        invoice.line_items.forEach((l) => m.set(l.id, l));
        return m;
    }, [invoice.line_items]);

    const alreadyCreditedByLine = useMemo(() => {
        // We don't have per-line CN totals in the invoice enrichment today, so assume 0;
        // backend re-validates on submit and blocks over-credit with a toast-friendly 400.
        return new Map();
    }, []);

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/billing/credit-note/reasons");
                setReasons(data.items);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load reasons"));
            }
        })();
        // Pre-seed one blank line tied to the first invoice line
        const seedLines = [{
            source_invoice_line_id: invoice.line_items[0]?.id || "",
            description: "",
            hsn_sac_code: "",
            quantity: 1,
            amount: 0,
            gst_percent: invoice.line_items[0]?.gst_percent ?? 18,
        }];
        setLines(seedLines);
        setReasonCode("price_revision");
        setReasonNotes("");
        // Baseline so an immediate outside-click closes cleanly
        baselineRef.current = JSON.stringify({
            reasonCode: "price_revision",
            reasonNotes: "",
            lines: seedLines,
        });
    }, [open, invoice.line_items]);

    const addLine = () => {
        const first = invoice.line_items[0];
        setLines((p) => [...p, {
            source_invoice_line_id: first?.id || "",
            description: "",
            hsn_sac_code: "",
            quantity: 1,
            amount: 0,
            gst_percent: first?.gst_percent ?? 18,
        }]);
    };
    const updateLine = (idx, key, value) => {
        setLines((p) => p.map((l, i) => {
            if (i !== idx) return l;
            const next = { ...l, [key]: value };
            // When source line changes, sync gst_percent + hsn and reset desc override
            if (key === "source_invoice_line_id") {
                const src = sourceById.get(value);
                if (src) {
                    next.gst_percent = src.gst_percent;
                    next.hsn_sac_code = "";
                    next.description = "";
                }
            }
            return next;
        }));
    };
    const removeLine = (idx) => setLines((p) => p.filter((_, i) => i !== idx));

    const totals = useMemo(() => {
        let sub = 0, tax = 0;
        for (const l of lines) {
            const a = Number(l.amount) || 0;
            const g = Number(l.gst_percent) || 0;
            sub += a;
            tax += a * g / 100;
        }
        return { sub, tax, total: sub + tax };
    }, [lines]);

    const submit = async () => {
        if (!reasonNotes.trim() || reasonNotes.trim().length < 4) {
            toast.error("Please enter reason notes (min 4 chars) — this is a legal document");
            return;
        }
        if (lines.length === 0 || lines.some((l) => !l.source_invoice_line_id || Number(l.amount) <= 0)) {
            toast.error("Each credit line needs a source invoice line + positive amount");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                invoice_id: invoice.id,
                reason_code: reasonCode,
                reason_notes: reasonNotes.trim(),
                line_items: lines.map((l) => ({
                    source_invoice_line_id: l.source_invoice_line_id,
                    description: l.description?.trim() || null,
                    hsn_sac_code: l.hsn_sac_code?.trim() || null,
                    quantity: Number(l.quantity) || 1,
                    amount: Number(l.amount),
                    gst_percent: Number(l.gst_percent) || 0,
                })),
            };
            const { data } = await api.post("/billing/credit-note/create", payload);
            toast.success(`Credit Note ${data.cn_number} issued for ${formatINR(data.total_amount, invoice?.currency)}`);
            baselineRef.current = formSnapshot;  // bypass discard prompt
            setOpen(false);
            onIssued?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to issue credit note"));
        } finally {
            setSubmitting(false);
        }
    };

    const fullyCredited = invoice.outstanding + (invoice.credit_note_total || 0) - (invoice.paid_amount || 0) <= 0
        && (invoice.credit_note_total || 0) >= invoice.total_amount - 0.01;

    return (
        <>
            <Button
                variant="outline"
                disabled={fullyCredited}
                onClick={() => setOpen(true)}
                className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                data-testid="issue-cn-btn"
            >
                <ArrowsClockwise size={14} /> Issue credit note
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-3xl"
                testId="issue-cn-dialog"
                title={`Issue credit note · ${invoice.invoice_number}`}
                description="Pick one or more original invoice lines and set the credit amount. GST rate is locked to the source line rate. CN will be numbered and immutable the moment you submit."
            >
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Reason</Label>
                        <select
                            value={reasonCode}
                            onChange={(e) => setReasonCode(e.target.value)}
                            className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white"
                            data-testid="cn-reason-select"
                        >
                            {reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Outstanding today</Label>
                        <div className="mt-1.5 h-10 px-3 flex items-center border border-slate-200 rounded-md bg-slate-50 font-mono text-sm">
                            {formatINR(invoice.outstanding, invoice?.currency)}
                        </div>
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Reason notes (required, min 4 chars)</Label>
                    <Textarea
                        value={reasonNotes}
                        onChange={(e) => setReasonNotes(e.target.value)}
                        rows={2}
                        placeholder="e.g. Post-sale discount approved by RM on 01-May-2026 via email."
                        className="mt-1.5"
                        data-testid="cn-reason-notes-input"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Credit lines</Label>
                        <Button type="button" variant="ghost" size="sm" onClick={addLine} className="gap-1 text-slate-600" data-testid="cn-add-line-btn">
                            <Plus size={12} /> Add line
                        </Button>
                    </div>
                    <div className="mt-1.5 space-y-2">
                        {lines.map((l, idx) => {
                            const src = sourceById.get(l.source_invoice_line_id);
                            const max = src ? src.amount : 0;
                            return (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`cn-line-${idx}`}>
                                    <select
                                        className="col-span-5 h-10 px-2 text-sm border border-slate-200 rounded-md bg-white truncate"
                                        value={l.source_invoice_line_id}
                                        onChange={(e) => updateLine(idx, "source_invoice_line_id", e.target.value)}
                                        data-testid={`cn-line-source-${idx}`}
                                    >
                                        {invoice.line_items.map((il) => (
                                            <option key={il.id} value={il.id}>
                                                {il.description} — {formatINR(il.amount, invoice?.currency)} @ {il.gst_percent}%
                                            </option>
                                        ))}
                                    </select>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max={max}
                                        value={l.amount}
                                        onChange={(e) => updateLine(idx, "amount", e.target.value)}
                                        className="col-span-3 font-mono"
                                        placeholder="Credit amount"
                                        data-testid={`cn-line-amount-${idx}`}
                                    />
                                    <div className="col-span-2 text-[11px] text-slate-600 text-center">
                                        <div>GST {l.gst_percent}%</div>
                                        <div className="text-slate-400 font-mono text-[10px]">max {formatINR(max, invoice?.currency)}</div>
                                    </div>
                                    <Input
                                        type="number"
                                        min="1"
                                        step="1"
                                        value={l.quantity}
                                        onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                                        className="col-span-1 font-mono"
                                        placeholder="Qty"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        disabled={lines.length === 1}
                                        onClick={() => removeLine(idx)}
                                        className="col-span-1 text-slate-400 hover:text-rose-600 h-9"
                                        data-testid={`cn-line-remove-${idx}`}
                                    >
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 text-right text-[11px] text-slate-600 space-y-0.5" data-testid="cn-totals-preview">
                        <div>Subtotal: <span className="font-mono font-semibold">{formatINR(totals.sub, invoice?.currency)}</span></div>
                        <div>GST: <span className="font-mono font-semibold">{formatINR(totals.tax, invoice?.currency)}</span></div>
                        <div className="text-sm text-slate-900 font-display font-bold">
                            Total credit: <span className="font-mono">{formatINR(totals.total, invoice?.currency)}</span>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <FormDialogCancel testId="cn-cancel-btn">Cancel</FormDialogCancel>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
                        data-testid="cn-submit-btn"
                    >
                        <ArrowsClockwise size={14} /> {submitting ? "Issuing…" : "Issue credit note"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

function SettlementContextBanner({ invoice }) {
    // Iter 51.9 — additive visibility helper. Pulls four already-computed
    // fields from the enriched invoice payload (no extra API calls):
    //   - total_amount          (invoice gross — includes GST)
    //   - paid_amount           (sum of cash payments already recorded)
    //   - tds_amount            (sum of TDS already recorded as credit)
    //   - credit_note_total     (credits applied to this invoice)
    //   - outstanding           (the remainder — already shown elsewhere)
    // Behaviour: ALWAYS shows Invoice total + Outstanding. The Cash /
    // TDS / Credit-note breakdown rows render ONLY when their value is
    // > 0 — so a freshly-issued invoice shows just total + outstanding
    // without noise, but as soon as something has been settled it
    // surfaces inline so the operator can never miss it.
    const total = Number(invoice?.total_amount || 0);
    const paid = Number(invoice?.paid_amount || 0);
    const tds = Number(invoice?.tds_amount || 0);
    const credited = Number(invoice?.credit_note_total || 0);
    const outstanding = Number(invoice?.outstanding || 0);
    const settled = paid + tds + credited;
    const ccy = invoice?.currency;
    const hasPriorSettlement = settled > 0.01;

    return (
        <div
            className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2.5 mb-3"
            data-testid="payment-settlement-context"
        >
            <div className="reedos-eyebrow text-[10px] text-slate-500 mb-1.5">
                Settlement context
            </div>
            <dl className="space-y-1 text-[12px]">
                <Row
                    label="Invoice total (incl. GST)"
                    value={formatINR(total, ccy)}
                    tone="slate"
                    testid="settlement-total"
                />
                {paid > 0.01 && (
                    <Row
                        label="Cash already received"
                        value={formatINR(paid, ccy)}
                        tone="emerald"
                        testid="settlement-paid"
                    />
                )}
                {tds > 0.01 && (
                    <Row
                        label="TDS already recorded"
                        value={formatINR(tds, ccy)}
                        tone="amber"
                        testid="settlement-tds"
                    />
                )}
                {credited > 0.01 && (
                    <Row
                        label="Credit notes applied"
                        value={`(${formatINR(credited, ccy)})`}
                        tone="rose"
                        testid="settlement-credit"
                    />
                )}
                {hasPriorSettlement && (
                    <div className="border-t border-slate-200 pt-1 mt-1.5">
                        <Row
                            label="Already settled"
                            value={formatINR(settled, ccy)}
                            tone="emerald"
                            bold
                            testid="settlement-already"
                        />
                    </div>
                )}
                <div className={hasPriorSettlement ? "" : "border-t border-slate-200 pt-1 mt-1.5"}>
                    <Row
                        label="Collection Outstanding"
                        value={formatINR(outstanding, ccy)}
                        tone={outstanding <= 0.01 ? "emerald" : "slate"}
                        bold
                        testid="settlement-outstanding"
                    />
                </div>
            </dl>
        </div>
    );
}


function Row({ label, value, tone = "slate", bold = false, testid }) {
    const TONE = {
        slate: "text-slate-700",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
    };
    return (
        <div className="flex items-center justify-between" data-testid={testid}>
            <dt className={`text-[11px] ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>
                {label}
            </dt>
            <dd className={`font-mono ${bold ? "font-bold" : ""} ${TONE[tone]}`}>{value}</dd>
        </div>
    );
}


function AddPaymentDialog({ invoice, onAdded, onSwitchToAttachTds }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Iter 52 — REEDOS Settlement Record model. Each settlement is ONE
    // event with multiple components: cash + TDS. The reconciliation
    // formula displayed inline is:
    //
    //     Billing Amount + GST − TDS = Net Cash Received
    //     Net Cash Received + TDS Deducted = Gross Settlement
    //
    // For partial settlements, the operator can drive any of the three
    // inputs and the others auto-reconcile.
    const subtotal = Number(invoice?.subtotal || 0);        // Billing Amount (ex-GST)
    const gst = Number(invoice?.tax_amount || 0);            // GST
    const grossInvoice = Number(invoice?.total_amount || 0); // Gross Invoice (incl. GST)
    const outstanding = Number(invoice?.outstanding || 0);

    // ── Iter 55 — Smart hint: did the operator likely mean "Attach TDS"? ──
    // When the outstanding on an invoice could plausibly be TDS on an
    // ALREADY-RECORDED cash settlement (i.e. there's a payment row with
    // amount > 0 and tds_amount = 0), the cleaner action is to ATTACH
    // TDS to that row rather than create a brand-new settlement that
    // happens to be TDS-only. This soft hint nudges the operator there
    // without blocking them — staged / multi-settlement scenarios remain
    // fully supported.
    const tdsHintTarget = (() => {
        const pays = Array.isArray(invoice?.payments) ? invoice.payments : [];
        // Iter 80 — voided rows are reversed; don't hint at them.
        const candidates = pays.filter((p) => !p.voided_at && Number(p.amount || 0) > 0 && Number(p.tds_amount || 0) === 0);
        if (candidates.length === 0) return null;
        // Sort by created_at desc, most recent first.
        const sorted = [...candidates].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        const p = sorted[0];
        // Outstanding must be > 0 (else no dialog opens at all) AND
        // plausibly ≤ 30% of the gross invoice — typical TDS bracket.
        // If outstanding > 30%, the operator probably IS recording a
        // genuine second settlement (e.g. milestone billing) and we
        // shouldn't nudge.
        if (outstanding <= 0.01 || grossInvoice <= 0) return null;
        const outstandingShare = outstanding / grossInvoice;
        if (outstandingShare > 0.30) return null;
        return p;
    })();

    const initialForm = () => {
        // Default: settle the full outstanding amount. Default TDS% = 10
        // (most common 194J case). Operator can override for 194C / lower
        // deduction cert / NIL / PAN-related cases.
        const defaultTdsPct = invoice?.gst_applicable !== false ? 10 : 0;
        const defaultTds = Math.round((subtotal * defaultTdsPct / 100) * 100) / 100;
        const defaultCash = Math.max(0, Math.round((outstanding - defaultTds) * 100) / 100);
        return {
            amount: defaultCash,
            tds_amount: defaultTds,
            tds_percentage: defaultTdsPct,
            tds_status: "Deducted",
            payment_mode: "NEFT",
            reference_number: "",
            payment_date: new Date().toISOString().slice(0, 10),
            remarks: "",
            // Iter 61 — Cross-currency receipt capture. Off by default;
            // operator opts in via a toggle when invoice currency ≠ receipt currency.
            cross_currency_enabled: false,
            receipt_currency: "INR",
            receipt_amount: "",
        };
    };
    const [form, setForm] = useState(initialForm);
    const baselineRef = useRef(JSON.stringify(form));

    useEffect(() => {
        if (open) {
            const next = initialForm();
            setForm(next);
            baselineRef.current = JSON.stringify(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, outstanding]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    // Iter 52 — TDS % drives TDS Amount + Net Cash Received together.
    // Operator can later override either independently for special cases
    // (lower-deduction cert, NIL deduction, PAN-not-furnished higher rate).
    const onTdsPercentChange = (pct) => {
        const numericPct = Number(pct) || 0;
        const tds = Math.round((subtotal * numericPct / 100) * 100) / 100;
        const remaining = Math.max(0, Math.round((outstanding - tds) * 100) / 100);
        setForm((f) => ({ ...f, tds_percentage: numericPct, tds_amount: tds, amount: remaining }));
    };

    // When operator manually overrides TDS Amount, recompute Net Cash.
    // TDS % gets a "Custom" indicator since it no longer matches Amount.
    const onTdsAmountChange = (val) => {
        const newTds = Number(val) || 0;
        const remaining = Math.max(0, Math.round((outstanding - newTds) * 100) / 100);
        setForm((f) => ({ ...f, tds_amount: newTds, amount: remaining }));
    };

    // When operator overrides Net Cash directly (e.g., partial settlement).
    const onCashChange = (val) => {
        setForm((f) => ({ ...f, amount: val }));
    };

    const cash = Number(form.amount) || 0;
    const tds = Number(form.tds_amount) || 0;
    const grossSettlement = round2(cash + tds);
    const exceeds = grossSettlement - outstanding > 0.01;
    const zeroGross = grossSettlement <= 0.01;
    // Iter 52 — flag "Custom" if TDS Amount doesn't match what TDS% on the
    // Billing Amount would yield (signals operator manually overrode the
    // amount, e.g., for a lower-deduction certificate).
    const tdsFromPct = round2(subtotal * (Number(form.tds_percentage) || 0) / 100);
    const tdsIsCustom = Math.abs(tdsFromPct - tds) > 0.01;

    // Iter 61 — Cross-currency receipt validation (UI mirror of backend).
    // Outstanding / over-settle math operates on ``amount`` only — the
    // receipt fields are stored verbatim for ledger reconciliation.
    const invoiceCurrency = (invoice?.currency || "INR").toUpperCase();
    const crossCurrencyOn = !!form.cross_currency_enabled;
    const receiptCcy = (form.receipt_currency || "").toUpperCase();
    const receiptAmt = Number(form.receipt_amount) || 0;
    const crossCurrencySameCcy = crossCurrencyOn && receiptCcy === invoiceCurrency;
    const crossCurrencyMissingAmount = crossCurrencyOn && !crossCurrencySameCcy && receiptAmt <= 0;

    const submit = async () => {
        if (zeroGross) { toast.error("Gross Settlement must be > 0 (cash + TDS)"); return; }
        if (exceeds) { toast.error("Gross Settlement exceeds outstanding"); return; }
        if (crossCurrencyMissingAmount) {
            toast.error("Receipt amount is required when receipt currency differs from invoice");
            return;
        }
        if (crossCurrencySameCcy) {
            toast.error("Receipt currency must differ from the invoice currency, or turn off the toggle");
            return;
        }
        setSubmitting(true);
        try {
            // Iter 52 — use the `/settlement/add` alias for new code paths.
            // (Old `/payment/add` continues to work as a hard alias.)
            const body = {
                amount: cash,
                tds_amount: tds,
                tds_percentage: Number(form.tds_percentage) || 0,
                tds_status: form.tds_status,
                payment_mode: form.payment_mode,
                reference_number: form.reference_number,
                remarks: form.remarks,
                payment_date: new Date(form.payment_date).toISOString(),
            };
            // Iter 61 — only attach the receipt fields when the toggle is
            // ON and a non-zero amount in a different currency was entered.
            // Keeps the payload identical to pre-Iter-61 for the 99% case.
            if (crossCurrencyOn && receiptAmt > 0 && receiptCcy && receiptCcy !== invoiceCurrency) {
                body.receipt_currency = receiptCcy;
                body.receipt_amount = receiptAmt;
            }
            await api.post(`/billing/settlement/add/${invoice.id}`, body);
            toast.success(
                body.receipt_currency
                    ? `Settlement ${formatINR(grossSettlement, invoiceCurrency)} recorded · ${receiptCcy} ${receiptAmt.toLocaleString("en-IN")} actually received`
                    : (tds > 0
                        ? `Settlement ${formatINR(grossSettlement, invoice?.currency)} recorded · cash ${formatINR(cash, invoice?.currency)} + TDS ${formatINR(tds, invoice?.currency)}`
                        : `Settlement ${formatINR(grossSettlement, invoice?.currency)} recorded`)
            );
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            setOpen(false);
            onAdded?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to record settlement"));
        } finally {
            setSubmitting(false);
        }
    };

    const fullyPaid = outstanding <= 0;

    return (
        <>
            <Button
                disabled={fullyPaid}
                onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
                data-testid="add-payment-btn"
            >
                <HandCoins size={14} /> Record settlement
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                testId="add-payment-dialog"
                title="Record settlement"
                description={<>Collection Outstanding: <span className="font-mono font-semibold text-slate-900">{formatINR(outstanding, invoice?.currency)}</span></>}
            >
                {/* Iter 51.9 — Settlement context banner (prior settlements). */}
                <SettlementContextBanner invoice={invoice} />

                {/* ── Iter 55 — Soft hint: this might be TDS on an existing row ── */}
                {tdsHintTarget && onSwitchToAttachTds && (
                    <div
                        className="rounded-md border border-blue-200 bg-blue-50/70 px-3 py-2.5 flex items-start gap-2"
                        data-testid="tds-attach-hint"
                    >
                        <span className="text-blue-600 text-base leading-none mt-0.5">ⓘ</span>
                        <div className="flex-1 text-[12px] leading-snug text-blue-900">
                            <span className="font-semibold">Looks like this might be TDS deducted on an existing settlement.</span>{" "}
                            Settlement{" "}
                            <span className="font-mono font-semibold">{tdsHintTarget.id}</span>{" "}
                            of <span className="font-mono font-semibold">{formatINR(tdsHintTarget.amount, invoice?.currency)}</span>{" "}
                            has no TDS attached yet. Attaching TDS to that row keeps the invoice as ONE settlement event instead of creating a separate "TDS settlement" row.
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    // Defer to next tick so the close animation completes before the
                                    // Adjust dialog opens — avoids visual layering jitter.
                                    setTimeout(() => onSwitchToAttachTds(tdsHintTarget), 80);
                                }}
                                className="ml-1 underline underline-offset-2 font-semibold text-blue-800 hover:text-blue-950"
                                data-testid="tds-attach-hint-shortcut"
                            >
                                Attach TDS to {tdsHintTarget.id} instead →
                            </button>
                        </div>
                    </div>
                )}

                {/* ── Iter 52 — REEDOS Settlement reconciliation formula ── */}
                <div
                    className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-3 space-y-2"
                    data-testid="reedos-settlement-formula"
                >
                    <div className="reedos-eyebrow text-[10px] text-indigo-800 flex items-baseline justify-between">
                        <span>REEDOS settlement formula</span>
                        <span className="text-[9px] font-normal italic text-indigo-700/70 normal-case">
                            Billing + GST − TDS = Net cash · Cash + TDS = Gross settlement
                        </span>
                    </div>

                    <dl className="text-[12px] space-y-1">
                        <FormulaRow label="Billing amount (ex-GST)" value={formatINR(subtotal, invoice?.currency)} testid="formula-billing" />
                        {invoice?.gst_applicable !== false && (
                            <FormulaRow label="GST" value={formatINR(gst, invoice?.currency)} testid="formula-gst" />
                        )}
                        <FormulaRow label="Gross invoice" value={formatINR(grossInvoice, invoice?.currency)} bold testid="formula-gross-invoice" divider />

                        {/* Editable TDS row */}
                        <div className="flex items-center justify-between pt-1" data-testid="formula-tds-row">
                            <dt className="text-[11px] font-semibold text-amber-900 flex items-center gap-2">
                                TDS
                                <span className="inline-flex items-center gap-1">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="30"
                                        value={form.tds_percentage}
                                        onChange={(e) => onTdsPercentChange(e.target.value)}
                                        className="h-6 w-14 px-1.5 text-[11px] font-mono text-center bg-white border-amber-200"
                                        data-testid="payment-tds-percentage-input"
                                    />
                                    <span className="text-amber-700">%</span>
                                </span>
                                {tdsIsCustom && tds > 0 && (
                                    <span
                                        className="text-[9px] uppercase font-bold text-amber-800 bg-amber-200 px-1 py-0.5 rounded"
                                        title="TDS Amount has been manually overridden — does not match TDS % × Billing Amount"
                                    >
                                        Custom
                                    </span>
                                )}
                            </dt>
                            <dd className="flex items-center gap-1">
                                <span className="text-amber-700 text-[11px]">−</span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.tds_amount}
                                    onChange={(e) => onTdsAmountChange(e.target.value)}
                                    className="h-7 w-32 px-2 text-[12px] font-mono text-right bg-white border-amber-200"
                                    data-testid="payment-tds-amount-input"
                                />
                            </dd>
                        </div>

                        <FormulaRow
                            label="Net cash received"
                            value={
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.amount}
                                    onChange={(e) => onCashChange(e.target.value)}
                                    className="h-7 w-32 px-2 text-[12px] font-mono text-right bg-white border-emerald-200"
                                    data-testid="payment-amount-input"
                                />
                            }
                            tone="emerald"
                            bold
                            divider
                        />

                        <FormulaRow
                            label="Gross settlement"
                            value={
                                <span className="font-mono font-bold text-[13px]">
                                    {formatINR(grossSettlement, invoice?.currency)}
                                </span>
                            }
                            tone={zeroGross || exceeds ? "rose" : "indigo"}
                            bold
                            testid="formula-gross-settlement"
                        />
                    </dl>

                    {(exceeds || zeroGross) && (
                        <div
                            className="text-[11px] text-rose-700 font-semibold border-t border-rose-200 pt-1.5"
                            data-testid="formula-validation"
                        >
                            {zeroGross
                                ? "Gross Settlement must be > 0 (cash + TDS together)"
                                : `Exceeds outstanding by ${formatINR(grossSettlement - outstanding, invoice?.currency)}`}
                        </div>
                    )}

                    <div className="text-[10px] text-indigo-700/70 italic border-t border-indigo-200 pt-1.5 leading-tight">
                        Override TDS % for 194J (10%), 194C (2%), lower-deduction certificate,
                        NIL deduction, or PAN-not-furnished higher-rate cases. Override TDS
                        Amount directly for non-standard certs.
                    </div>
                </div>

                {/* TDS lifecycle status (when TDS > 0) */}
                {tds > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold">TDS lifecycle</Label>
                            <select value={form.tds_status} onChange={(e) => setForm({ ...form, tds_status: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="payment-tds-status-select">
                                <option value="Deducted">Deducted (Form 16A pending)</option>
                                <option value="Received">Received (Form 16A in hand)</option>
                                <option value="Verified">Verified (26AS reconciled)</option>
                                <option value="Claimed">Claimed (offset against IT)</option>
                                <option value="Adjusted">Adjusted (lifecycle complete)</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Settlement date</Label>
                            <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" data-testid="payment-date-input" />
                        </div>
                    </div>
                )}

                {tds <= 0 && (
                    <div>
                        <Label className="text-xs font-semibold">Settlement date</Label>
                        <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1.5" data-testid="payment-date-input" />
                    </div>
                )}

                {/* Iter 61 — Cross-currency receipt capture.
                    Operator opts in when invoice currency ≠ actual receipt currency
                    (e.g. invoiced in AED, received in INR after bank conversion).
                    Outstanding/aging math stays on `amount` (invoice currency) —
                    the receipt fields are stored verbatim for ledger reconciliation. */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
                    <label className="flex items-start gap-2 cursor-pointer text-[12px] text-slate-800">
                        <input
                            type="checkbox"
                            checked={!!form.cross_currency_enabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                // Iter 61: when the toggle flips ON, snap receipt_currency
                                // to the FIRST option that differs from the invoice
                                // currency so the visible dropdown selection and the
                                // form-state value always agree (no false "must differ"
                                // error from a stale default).
                                const firstAlt = ["INR", "USD", "AED", "GBP", "EUR"]
                                    .find((c) => c !== invoiceCurrency) || "INR";
                                setForm({
                                    ...form,
                                    cross_currency_enabled: enabled,
                                    receipt_currency: enabled ? firstAlt : form.receipt_currency,
                                    receipt_amount: enabled ? form.receipt_amount : "",
                                });
                            }}
                            className="mt-0.5 accent-indigo-600"
                            data-testid="payment-cross-currency-toggle"
                        />
                        <span>
                            <b>Cash received in a different currency than invoice</b>
                            <span className="block text-[10px] text-slate-500 mt-0.5">
                                Invoice is in <span className="font-mono font-semibold">{invoiceCurrency}</span>.
                                Capture the actual receipt currency + amount for ledger reconciliation
                                (no FX rate, no gain/loss tracking).
                            </span>
                        </span>
                    </label>
                    {form.cross_currency_enabled && (
                        <div className="mt-2.5 grid grid-cols-2 gap-3" data-testid="payment-cross-currency-fields">
                            <div>
                                <Label className="text-[11px] font-semibold">Actual receipt currency</Label>
                                <select
                                    value={form.receipt_currency}
                                    onChange={(e) => setForm({ ...form, receipt_currency: e.target.value })}
                                    className="mt-1 w-full h-9 px-2 text-[12px] border border-slate-200 rounded-md bg-white"
                                    data-testid="payment-receipt-currency-select"
                                >
                                    {["INR", "USD", "AED", "GBP", "EUR"].filter((c) => c !== invoiceCurrency).map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <Label className="text-[11px] font-semibold">Actual receipt amount</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.receipt_amount}
                                    onChange={(e) => setForm({ ...form, receipt_amount: e.target.value })}
                                    placeholder={`e.g. 577150`}
                                    className="mt-1 h-9 font-mono text-right"
                                    data-testid="payment-receipt-amount-input"
                                />
                            </div>
                            <div className="col-span-2 text-[10.5px] text-slate-600 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="payment-cross-currency-preview">
                                <span className="font-semibold text-slate-700">Will record: </span>
                                <span className="font-mono">
                                    {invoiceCurrency} {grossSettlement.toLocaleString("en-IN")}
                                </span>
                                <span className="text-slate-400"> settles outstanding · actual receipt </span>
                                <span className="font-mono">
                                    {receiptCcy} {receiptAmt > 0 ? receiptAmt.toLocaleString("en-IN") : "—"}
                                </span>
                                <span className="text-slate-400"> kept for bank reconciliation.</span>
                            </div>
                            {crossCurrencyMissingAmount && (
                                <div className="col-span-2 text-[11px] text-rose-700 font-semibold" data-testid="payment-cross-currency-error">
                                    Receipt amount is required when receipt currency differs from invoice.
                                </div>
                            )}
                            {crossCurrencySameCcy && (
                                <div className="col-span-2 text-[11px] text-rose-700 font-semibold" data-testid="payment-cross-currency-same-error">
                                    Receipt currency must differ from the invoice currency, or turn off the toggle.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Mode</Label>
                        <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="payment-mode-select">
                            <option>NEFT</option><option>RTGS</option><option>IMPS</option><option>UPI</option><option>Cheque</option><option>Cash</option>
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Reference #</Label>
                        <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} placeholder="UTR / cheque #" className="mt-1.5 font-mono" data-testid="payment-reference-input" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Remarks</Label>
                    <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="mt-1.5" data-testid="payment-remarks-input" />
                </div>
                <DialogFooter>
                    <FormDialogCancel testId="payment-cancel-btn">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting || exceeds || zeroGross || crossCurrencyMissingAmount || crossCurrencySameCcy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="payment-submit-btn">
                        {submitting ? "Recording..." : "Record settlement"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}


function FormulaRow({ label, value, tone = "slate", bold = false, divider = false, testid }) {
    const TONE = {
        slate: "text-slate-700",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
        indigo: "text-indigo-700",
    };
    return (
        <div
            className={`flex items-center justify-between ${divider ? "border-t border-indigo-200/60 pt-1.5 mt-1" : ""}`}
            data-testid={testid}
        >
            <dt className={`text-[11px] ${bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{label}</dt>
            <dd className={`font-mono text-[12px] ${bold ? "font-bold" : ""} ${TONE[tone]}`}>{value}</dd>
        </div>
    );
}


function round2(n) {
    return Math.round((Number(n) || 0) * 100) / 100;
}

function SendReminderDialog({ invoice, onSent }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [template, setTemplate] = useState(invoice.days_overdue > 7 ? "collection_reminder_final" : "collection_reminder_first");
    const [toEmail, setToEmail] = useState(invoice.client?.contact_email || "");
    // Iter 58 — Collection actions (reminders) must be disabled on
    // economically-settled invoices. Both "Paid" and the new
    // "Fully Settled — Pending TDS Attachment" qualify — the latter still
    // has pending finance paperwork but the client owes nothing.
    const disabled = invoice.status === "Paid" || invoice.status === "Fully Settled — Pending TDS Attachment";

    const submit = async () => {
        if (!toEmail) { toast.error("Recipient email required"); return; }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/billing/invoice/${invoice.id}/send-reminder`, {
                template_code: template, to_email: toEmail,
            });
            if (data.mode === "dry_run") {
                toast.success("Reminder logged (email API key not set — dev fallback)");
            } else if (data.mode === "sent") {
                toast.success("Reminder sent");
            } else {
                toast.error(`Send failed: ${data.error || "unknown"}`);
            }
            setOpen(false);
            onSent?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to send reminder"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button disabled={disabled} variant="outline" className="gap-1.5" data-testid="send-reminder-btn">
                    <PaperPlaneTilt size={14} weight="fill" /> Send for collection
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-display">Send collection reminder</DialogTitle>
                    <DialogDescription>An email will be dispatched and logged on this invoice's timeline. If the Resend API key is not configured, the message is logged only (dev fallback).</DialogDescription>
                </DialogHeader>
                <div>
                    <Label className="text-xs font-semibold">Template</Label>
                    <select value={template} onChange={(e) => setTemplate(e.target.value)} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="reminder-template-select">
                        <option value="collection_reminder_first">First notice — friendly</option>
                        <option value="collection_reminder_final">{`Final notice — urgent (${invoice.days_overdue || 0}d overdue)`}</option>
                    </select>
                </div>
                <div>
                    <Label className="text-xs font-semibold">To</Label>
                    <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} placeholder="client billing contact" className="mt-1.5" data-testid="reminder-to-input" />
                    {invoice.client?.contact_name && (
                        <p className="text-[11px] text-slate-500 mt-1">Default: {invoice.client.contact_name} &lt;{invoice.client.contact_email}&gt;</p>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="reminder-submit-btn">
                        {submitting ? "Sending..." : "Send now"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DocumentSection({ invoice, onUploaded }) {
    const [uploading, setUploading] = useState(false);
    const handleFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("entity_type", "invoice");
            fd.append("entity_id", invoice.id);
            await api.post("/billing/document/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Uploaded");
            onUploaded?.();
        } catch (err) {
            toast.error(formatApiError(err, "Upload failed"));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };
    return (
        <div className="reedos-card" data-testid="invoice-documents-card">
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="reedos-eyebrow">Documents</div>
                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Invoice PDFs</h3>
            </div>
            <div className="p-5 space-y-3">
                {invoice.documents?.length > 0 ? (
                    invoice.documents.map((d) => (
                        <button key={d.id}
                            type="button"
                            onClick={async () => {
                                try { await previewDocument(d.id, { mime: d.content_type }); }
                                catch (err) { toast.error(formatApiError(err, "Preview failed")); }
                            }}
                            className="flex w-full items-center justify-between gap-3 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-900 text-left"
                            data-testid={`inv-doc-${d.id}`}
                        >
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
                                <div className="text-[11px] text-slate-500">{(d.size / 1024).toFixed(1)} KB · {formatDateTime(d.uploaded_at)}</div>
                            </div>
                            <FileArrowUp size={16} className="text-slate-400 shrink-0" />
                        </button>
                    ))
                ) : (
                    <div className="text-xs text-slate-500">No invoice PDFs uploaded.</div>
                )}
                <RoleGuard permission="invoice:write">
                    <label className="block border-2 border-dashed border-slate-200 bg-slate-50 rounded-md px-4 py-5 text-center cursor-pointer hover:border-slate-400 transition-colors">
                        <FileArrowUp size={20} className="mx-auto text-slate-400" />
                        <div className="text-xs text-slate-600 mt-1.5">{uploading ? "Uploading..." : "Click to upload PDF (≤15MB)"}</div>
                        <input type="file" className="hidden" onChange={handleFile} accept=".pdf,image/*" disabled={uploading} data-testid="invoice-upload-input" />
                    </label>
                </RoleGuard>
            </div>
        </div>
    );
}

const EVENT_ICON = {
    invoice_created: ReceiptIcon,
    payment_added: CurrencyInr,
    document_uploaded: FileTextIcon,
    reminder_sent: Bell,
    status_changed: LockKey,
};

const EVENT_TONE = {
    invoice_created: "bg-slate-900 text-white",
    payment_added: "bg-emerald-500 text-white",
    document_uploaded: "bg-slate-500 text-white",
    reminder_sent: "bg-amber-500 text-white",
    status_changed: "bg-slate-900 text-white",
};

function TimelinePanel({ events }) {
    if (!events?.length) {
        return (
            <div className="reedos-card" data-testid="invoice-timeline-card">
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="reedos-eyebrow flex items-center gap-1.5"><ClockCounterClockwise size={12} /> Timeline</div>
                    <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Activity</h3>
                </div>
                <div className="px-5 py-10 text-center text-sm text-slate-500">No events yet.</div>
            </div>
        );
    }
    return (
        <div className="reedos-card" data-testid="invoice-timeline-card">
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="reedos-eyebrow flex items-center gap-1.5"><ClockCounterClockwise size={12} /> Timeline</div>
                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{events.length} event{events.length > 1 ? "s" : ""}</h3>
            </div>
            <div className="p-5">
                <ol className="relative border-l border-slate-200 ml-3 space-y-5">
                    {events.map((e) => {
                        const Icon = EVENT_ICON[e.event_type] || ClockCounterClockwise;
                        const tone = EVENT_TONE[e.event_type] || "bg-slate-900 text-white";
                        return (
                            <li key={e.id} className="ml-6 relative" data-testid={`timeline-event-${e.id}`}>
                                <span className={"absolute -left-[34px] top-0 h-6 w-6 rounded-full flex items-center justify-center " + tone}>
                                    <Icon size={12} weight="fill" />
                                </span>
                                <div className="text-sm font-medium text-slate-900">{e.summary}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                    {formatDateTime(e.created_at)} · {e.actor_name}
                                </div>
                                {e.metadata?.provider_message_id && (
                                    <div className="text-[10px] text-slate-400 mt-0.5 font-mono">msg {e.metadata.provider_message_id}</div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}


/* ------------------------------------------------------------------ */
/* Iter 64 — Cancellation banner + Uncancel control                    */
/* ------------------------------------------------------------------ */

/**
 * Renders the rose banner at the top of a cancelled invoice + a diagonal
 * "CANCELLED" watermark across the page so the operator immediately
 * grasps the statutory state, the reason, and who voided it.
 *
 * The diagonal watermark is `position:fixed` with pointer-events:none so
 * it overlays everything (including the actions row) but never blocks
 * interactions with the underlying controls.
 */
function CancelledInvoiceBanner({ invoice }) {
    const cancelledByName = invoice.cancelled_by?.name || invoice.cancelled_by?.email || "—";
    return (
        <>
            {/* Diagonal watermark — always visible across the invoice surface. */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 z-30 flex items-center justify-center select-none"
                data-testid="cancelled-watermark"
            >
                <div className="-rotate-[28deg] text-rose-600/15 font-black tracking-[0.3em] text-[140px] sm:text-[200px] whitespace-nowrap">
                    CANCELLED
                </div>
            </div>

            <div className="px-6 sm:px-8 pt-4">
                <div
                    className="rounded-md border border-rose-300 bg-rose-50 text-rose-900 px-4 py-3 flex items-start gap-3"
                    data-testid="cancelled-banner"
                >
                    <Prohibit size={20} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-1">
                        <div className="text-sm font-semibold">
                            This invoice was cancelled on {formatDateTime(invoice.cancelled_at)} by {cancelledByName}
                        </div>
                        <div className="text-xs leading-relaxed">
                            <span className="font-semibold">Reason:</span>{" "}
                            {invoice.cancellation_reason_label || invoice.cancellation_reason || "—"}
                            {invoice.cancellation_note && (
                                <span> · <span className="font-semibold">Note:</span> {invoice.cancellation_note}</span>
                            )}
                        </div>
                        <div className="text-[11px] text-rose-900/70 pt-0.5">
                            Statutorily inactive — excluded from receivables, aging, reminders, GST reports,
                            TDS reports, forecasts, and dashboard totals. Pre-cancellation status was{" "}
                            <span className="font-mono bg-white border border-rose-200 px-1 rounded">
                                {invoice.pre_cancellation_status || "Unpaid"}
                            </span>.
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/**
 * Primary-Admin-only restore control. Visible only when:
 *   1. invoice.status === "Cancelled"
 *   2. We're within the 24-hour reopen window (computed client-side
 *      from cancelled_at; server enforces the same gate authoritatively).
 *
 * Confirmation is intentionally minimal — the consequential decision
 * was already made at cancellation; restoration is the "I clicked Cancel
 * by mistake" escape valve and we don't want a friction-laden flow.
 */
function UncancelInvoiceButton({ invoice, onRestored }) {
    const [submitting, setSubmitting] = useState(false);

    const cancelledAt = invoice.cancelled_at ? new Date(invoice.cancelled_at) : null;
    const ageHours = cancelledAt
        ? (Date.now() - cancelledAt.getTime()) / 3_600_000
        : Infinity;
    const withinWindow = ageHours < 24;

    if (!withinWindow) {
        return (
            <span
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium border bg-slate-100 text-slate-500 border-slate-200"
                title={`Uncancel window expired (${ageHours.toFixed(1)}h ago — limit 24h)`}
                data-testid="uncancel-expired-tag"
            >
                <Clock size={12} /> Uncancel window expired
            </span>
        );
    }

    const submit = async () => {
        if (!window.confirm(
            `Restore invoice ${invoice.invoice_number}? This will move it back to status `
            + `"${invoice.pre_cancellation_status || "Unpaid"}" and (for AMC invoices) re-advance the linked schedule. `
            + `Audit log retains the full cancel→restore trail.`
        )) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/billing/invoice/${invoice.id}/uncancel`);
            toast.success(
                `Invoice ${data.invoice_number || invoice.invoice_number} restored.`,
                { description: `Status: ${data.restored_to_status}. Window used: ${data.age_hours_at_uncancel}h of 24h.` },
            );
            onRestored?.();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to restore invoice"));
        } finally {
            setSubmitting(false);
        }
    };

    const hoursLeft = (24 - ageHours).toFixed(1);
    return (
        <Button
            variant="outline"
            onClick={submit}
            disabled={submitting}
            className="gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="uncancel-invoice-btn"
            title={`Primary-Admin reopen window — ${hoursLeft}h remaining`}
        >
            <ArrowCounterClockwise size={14} weight="bold" />
            {submitting ? "Restoring…" : `Restore (${hoursLeft}h left)`}
        </Button>
    );
}


/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */
export default function InvoiceDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isPrimaryAdmin = !!user?.is_primary_admin;
    const [invoice, setInvoice] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adjustPayment, setAdjustPayment] = useState(null);
    // Iter 60 — Primary-Admin-only data-entry correction dialog.
    const [correctPayment, setCorrectPayment] = useState(null);
    // Iter 80 — Primary-Admin-only payment void + date-edit dialogs.
    const [voidPayment, setVoidPayment] = useState(null);
    const [editDatePayment, setEditDatePayment] = useState(null);
    // UAT-003 — Primary-Admin-only invoice date-edit dialog.
    const [editDateOpen, setEditDateOpen] = useState(false);
    // Iter 80 — track which payment row's "More" dropdown is open. Single
    // mutually-exclusive opener per Receipts log.
    const [openRowMenu, setOpenRowMenu] = useState(null);

    const load = async () => {
        try {
            const [{ data }, ci] = await Promise.all([
                api.get(`/billing/invoice/${id}`),
                api.get("/billing/company-info").catch(() => ({ data: null })),
            ]);
            setInvoice(data);
            if (ci?.data) setCompany(ci.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load invoice"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    if (loading) {
        return <div data-testid="invoice-detail-page"><PageHeader eyebrow="Invoice" title="Loading..." /></div>;
    }
    if (!invoice) return <div className="p-8 text-sm text-slate-500">Invoice not found.</div>;

    // International / non-GST invoices have `gst_breakup = null`. Use optional
    // chaining so reading isIntra never crashes; the GST breakdown block below
    // is independently guarded by `tax_amount > 0` and won't render anyway.
    const isIntra = (invoice.gst_breakup?.cgst || 0) > 0 || (invoice.gst_breakup?.sgst || 0) > 0;

    return (
        <div data-testid="invoice-detail-page">
            <PageHeader
                eyebrow={
                    <Link to="/invoices" className="inline-flex items-center gap-1 hover:text-slate-900">
                        <ArrowLeft size={12} /> Invoices
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-2xl sm:text-3xl">{invoice.invoice_number}</span>
                        {invoice.invoice_label && (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border bg-indigo-50 text-indigo-800 border-indigo-200" data-testid="invoice-label-badge">
                                {invoice.invoice_label}
                            </span>
                        )}
                        {invoice.billing_source === "offline_recorded" && (
                            <span
                                className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-semibold border bg-amber-50 text-amber-800 border-amber-200"
                                title={invoice.external_invoice_number ? `External ref: ${invoice.external_invoice_number}` : "Recorded from an external system"}
                                data-testid="invoice-offline-badge"
                            >
                                Offline · External
                            </span>
                        )}
                        <StatusBadge status={invoice.status} compact data-testid="invoice-status-badge" />
                        {/* UAT-003 — MIGRATION CORRECTION badge surfaces directly from the
                            invoice's `migration_mode_corrected_at` flag, mirroring the
                            payment-row badge in the Receipts log. Persists forever — even
                            after Migration Mode sign-off — for forensic traceability. */}
                        {invoice.migration_mode_corrected && (
                            <span
                                className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                                title="The invoice date was corrected during the tenant's Migration Mode window — see Audit Logs for the full forensic envelope"
                                data-testid="invoice-mcm-chip"
                            >
                                <Database size={10} weight="fill" /> Migration Correction
                            </span>
                        )}
                        {invoice.is_overdue && (
                            <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-semibold border bg-rose-50 text-rose-700 border-rose-200" data-testid="invoice-overdue-badge">
                                <Clock size={11} weight="fill" /> {invoice.days_overdue} days overdue
                            </span>
                        )}
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2">
                        <Buildings size={14} /> {invoice.client?.name}
                        {invoice.client?.gstin && <span className="font-mono text-xs ml-1.5 text-slate-400">· {invoice.client.gstin}</span>}
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/invoices/${invoice.id}/print`}>
                            <Button variant="outline" className="gap-1.5" data-testid="print-btn">
                                <Printer size={14} weight="fill" /> Print / PDF
                            </Button>
                        </Link>
                        {/* Iter 64 — Cancel + Uncancel are mutually exclusive. Cancel
                            renders for non-cancelled invoices (gated server-side too).
                            Uncancel renders only when cancelled AND user is Primary Admin
                            AND we are inside the 24-hour reopen window. */}
                        {invoice.status !== "Cancelled" && (
                            <RoleGuard permission="invoice:cancel">
                                <CancelInvoiceDialog invoice={invoice} onCancelled={load} />
                            </RoleGuard>
                        )}
                        {invoice.status === "Cancelled" && isPrimaryAdmin && (
                            <UncancelInvoiceButton invoice={invoice} onRestored={load} />
                        )}
                        {/* UAT-003 — Primary-Admin-only Edit Invoice Date action.
                            Hidden when the invoice is cancelled or GST-Filed (server
                            also enforces the gate authoritatively). */}
                        {invoice.status !== "Cancelled"
                            && isPrimaryAdmin
                            && (invoice.gst_filing_status || "Not Filed") === "Not Filed" && (
                            <Button
                                variant="outline"
                                className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
                                onClick={() => setEditDateOpen(true)}
                                data-testid="edit-invoice-date-btn"
                            >
                                <CalendarBlank size={14} weight="fill" /> Edit date
                            </Button>
                        )}
                        {invoice.status !== "Cancelled" && (
                            <>
                                <RoleGuard permission="reminder:send">
                                    <SendReminderDialog invoice={invoice} onSent={load} />
                                </RoleGuard>
                                <RoleGuard permission="invoice:write">
                                    <IssueCreditNoteDialog invoice={invoice} onIssued={load} />
                                </RoleGuard>
                                <RoleGuard permission="payment:write">
                                    <AddPaymentDialog
                                        invoice={invoice}
                                        onAdded={load}
                                        onSwitchToAttachTds={(p) => setAdjustPayment(p)}
                                    />
                                </RoleGuard>
                            </>
                        )}
                    </div>
                }
            />

            {/* Iter 64 — Cancelled banner + diagonal watermark. Banner renders
                above the overdue banner; diagonal watermark is positioned
                absolutely over the invoice content so it's always visible. */}
            {invoice.status === "Cancelled" && (
                <CancelledInvoiceBanner invoice={invoice} />
            )}
            {/* Overdue banner */}
            {invoice.is_overdue && (
                <div className="px-6 sm:px-8 pt-4">
                    <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-900 px-4 py-3 flex items-start gap-3" data-testid="overdue-banner">
                        <WarningOctagon size={18} weight="fill" className="text-rose-600 mt-0.5 shrink-0" />
                        <div>
                            <div className="text-sm font-semibold">This invoice is {invoice.days_overdue} days overdue.</div>
                            <div className="text-xs mt-0.5">Due on {formatDate(invoice.due_date)}. Click "Send for collection" to dispatch a reminder — the action will be logged on the timeline and audit trail.</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="px-6 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-6">
                <div className="space-y-6">
                    {/* Meta */}
                    <div className="reedos-card">
                        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-200">
                            {[
                                { label: "Invoice date", value: formatDate(invoice.invoice_date) },
                                { label: "Due date", value: formatDate(invoice.due_date) },
                                { label: "Payment terms", value: invoice.payment_terms },
                                { label: "PO reference", value: invoice.po_id ? <Link to={`/purchase-orders/${invoice.po_id}`} className="font-mono text-slate-900 hover:underline">View PO</Link> : "Manual" },
                            ].map((c) => (
                                <div key={c.label} className="px-4 py-3">
                                    <div className="reedos-eyebrow">{c.label}</div>
                                    <div className="text-sm text-slate-900 mt-1">{c.value}</div>
                                </div>
                            ))}
                        </div>
                        {/* Iter 41: Client PO reference (externally issued — finance reconciliation key) */}
                        {(invoice.external_po_number || invoice.external_po_date) && (
                            <div
                                className="border-t border-slate-200 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 bg-slate-50/40"
                                data-testid="invoice-client-po"
                            >
                                <div>
                                    <div className="reedos-eyebrow">Client PO Number</div>
                                    <div className="text-sm text-slate-900 mt-0.5 font-mono" data-testid="invoice-client-po-number">
                                        {invoice.external_po_number || <span className="text-slate-400">—</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="reedos-eyebrow">Client PO Date</div>
                                    <div className="text-sm text-slate-900 mt-0.5" data-testid="invoice-client-po-date">
                                        {invoice.external_po_date ? formatDate(invoice.external_po_date) : <span className="text-slate-400">—</span>}
                                    </div>
                                </div>
                                <p className="text-[10px] text-slate-500 max-w-md leading-snug ml-auto">
                                    Customer-issued PO — used for finance reconciliation. Distinct from the internal system PO above.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Iter 37: Offline invoice details + attachment download */}
                    {invoice.billing_source === "offline_recorded" && (
                        <OfflineInvoiceCard invoice={invoice} />
                    )}

                    {/* Line items */}
                    <div className="reedos-card">
                        <div className="px-5 py-4 border-b border-slate-200">
                            <div className="reedos-eyebrow">Line items</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Charges &amp; taxes</h3>
                        </div>
                        <table className="w-full text-sm" data-testid="invoice-lines-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Description</th>
                                    <th className="px-5 py-3 reedos-eyebrow">HSN/SAC</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Amount</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">GST</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Tax</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.line_items.map((li) => (
                                    <tr key={li.id} className="border-t border-slate-100">
                                        <td className="px-5 py-3 text-slate-900">
                                            <div>{li.description}</div>
                                            {li.is_amc_period && (
                                                <div className="mt-1 pl-2 border-l-2 border-amber-200 space-y-0.5">
                                                    <div className="text-[11px] text-slate-600 font-medium" data-testid={`amc-charge-label-${li.id}`}>
                                                        {li.service_charge_label || "Service Charges"}
                                                    </div>
                                                    <div className="text-[11px] text-slate-500" data-testid={`amc-period-${li.id}`}>
                                                        <span className="text-slate-400 mr-1">Service Period:</span>
                                                        {formatDate(li.service_period_start)} → {formatDate(li.service_period_end)}
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-xs font-mono text-slate-600">{li.hsn_sac_code || "—"}</td>
                                        <td className="px-5 py-3 text-right reedos-data">{formatINR(li.amount, invoice?.currency)}</td>
                                        <td className="px-5 py-3 text-right text-xs text-slate-600">{li.gst_percent}%</td>
                                        <td className="px-5 py-3 text-right reedos-data text-slate-700">{formatINR(li.gst_amount, invoice?.currency)}</td>
                                        <td className="px-5 py-3 text-right reedos-data font-semibold">{formatINR(li.total_amount, invoice?.currency)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="border-t border-slate-200 bg-slate-50 px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="text-xs text-slate-500">
                                {invoice.notes && <><span className="reedos-eyebrow">Notes</span><p className="mt-1 text-slate-700">{invoice.notes}</p></>}
                            </div>
                            <div className="space-y-1.5">
                                {/* Iter 67 — Special-Offer-aware totals.
                                    Shows the same 5-row commercial summary
                                    as the Proposal / PO / PDF surfaces so
                                    the discount is visible end-to-end. The
                                    persisted ``subtotal`` IS already the
                                    discounted base, but the offer snapshot
                                    carries the standard_value_at_apply so
                                    we can reconstruct the Gross → Less Offer
                                    → Taxable narrative without losing the
                                    original commercial value. */}
                                <CommercialSummary
                                    subtotal={
                                        invoice.special_offer?.enabled
                                            ? Number(invoice.special_offer.standard_value_at_apply || invoice.subtotal || 0)
                                            : Number(invoice.subtotal || 0)
                                    }
                                    gst={Number(invoice.tax_amount || 0)}
                                    total={Number(invoice.total_amount || 0)}
                                    specialOffer={invoice.special_offer}
                                    currency={invoice?.currency}
                                    dense
                                    testIdPrefix="invoice-summary"
                                />
                                {/* GST split block — preserves CGST/SGST or IGST
                                    breakup under the totals when intra/inter
                                    state is identified. */}
                                {Number(invoice.tax_amount || 0) > 0 && (
                                    <div className="pt-1 border-t border-slate-100 -mt-1">
                                        {isIntra ? (
                                            <>
                                                <div className="flex justify-between text-xs"><span className="text-slate-500">CGST</span><span className="reedos-data">{formatINR(invoice.gst_breakup.cgst, invoice?.currency)}</span></div>
                                                <div className="flex justify-between text-xs"><span className="text-slate-500">SGST</span><span className="reedos-data">{formatINR(invoice.gst_breakup.sgst, invoice?.currency)}</span></div>
                                            </>
                                        ) : (
                                            <div className="flex justify-between text-xs"><span className="text-slate-500">IGST</span><span className="reedos-data">{formatINR(invoice.gst_breakup.igst, invoice?.currency)}</span></div>
                                        )}
                                    </div>
                                )}
                                {Number(invoice.credit_note_total) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 inline-flex items-center gap-1"><ArrowsClockwise size={11} /> Credit notes</span>
                                        <span className="reedos-data text-rose-700" data-testid="invoice-cn-total">- {formatINR(invoice.credit_note_total, invoice?.currency)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Paid</span>
                                    <span className="reedos-data text-emerald-700">{formatINR(invoice.paid_amount, invoice?.currency)}</span>
                                </div>
                                {Number(invoice.tds_amount) > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500 inline-flex items-center gap-1">TDS recorded</span>
                                        <span className="reedos-data text-indigo-700">{formatINR(invoice.tds_amount, invoice?.currency)}</span>
                                    </div>
                                )}
                                {/* Iter 58 — Collection Outstanding is the genuine receivable
                                    (= cash the client still owes). Distinct from
                                    Pending TDS Reconciliation (paperwork, not money). */}
                                <div className="flex justify-between text-sm font-display font-bold" data-testid="invoice-collection-outstanding">
                                    <span>Collection Outstanding</span>
                                    <span className={`reedos-data ${Number(invoice.collection_outstanding ?? invoice.outstanding) > 0.01 ? "text-rose-700" : "text-emerald-700"}`}>
                                        {formatINR(invoice.collection_outstanding ?? invoice.outstanding, invoice?.currency)}
                                    </span>
                                </div>
                                {Number(invoice.pending_tds_reconciliation) > 0.01 && (
                                    <div
                                        className="flex justify-between items-baseline text-[11px] -mt-1 pl-2"
                                        data-testid="invoice-pending-tds-reconciliation"
                                        title="TDS deducted by client (status: Deducted or Received). Already accounted in settlement; verification / claim pending. NOT a receivable."
                                    >
                                        <span className="text-teal-700 inline-flex items-center gap-1">
                                            <span className="inline-block w-1 h-1 bg-teal-400 rounded-full" />
                                            Pending TDS reconciliation
                                        </span>
                                        <span className="reedos-data text-teal-700 font-mono">{formatINR(invoice.pending_tds_reconciliation, invoice?.currency)}</span>
                                    </div>
                                )}
                                {invoice.amount_in_words && (
                                    <div className="text-[11px] text-slate-500 italic mt-2 pt-2 border-t border-slate-200" data-testid="amount-in-words">
                                        {invoice.amount_in_words}
                                    </div>
                                )}
                                {invoice.customer_po_ref && (
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        <span className="reedos-eyebrow">Customer PO:</span> <span className="font-mono ml-1">{invoice.customer_po_ref}</span>
                                    </div>
                                )}
                                {/* Iter 50: REEDOS Proposal No — originating reference, immutable */}
                                {invoice.proposal_number && (
                                    <div className="text-[11px] text-slate-500 mt-1" data-testid="invoice-reedos-proposal-no">
                                        <span className="reedos-eyebrow">REEDOS Proposal No:</span>{" "}
                                        {invoice.proposal_id ? (
                                            <Link to={`/proposals/${invoice.proposal_id}`} className="font-mono ml-1 hover:text-slate-900 hover:underline">
                                                {invoice.proposal_number}
                                            </Link>
                                        ) : (
                                            <span className="font-mono ml-1">{invoice.proposal_number}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Payments */}
                    <div className="reedos-card" data-testid="payments-section">
                        <div className="px-5 py-4 border-b border-slate-200">
                            <div className="reedos-eyebrow">Payments</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">Receipts log</h3>
                        </div>
                        {invoice.payments.length === 0 ? (
                            <div className="px-5 py-10 text-center text-sm text-slate-500">No payments recorded yet.</div>
                        ) : (
                            <div className="p-5 space-y-3">
                                {invoice.payments.map((p) => {
                                    const isVoided = !!p.voided_at;
                                    return (
                                    <div key={p.id} className={"flex items-start gap-3 " + (isVoided ? "opacity-60" : "")} data-testid={`payment-row-${p.id}`}>
                                        <div className={"mt-1 h-2 w-2 rounded-full " + (isVoided ? "bg-rose-400" : "bg-emerald-500")}></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 flex-wrap">
                                                <span className={"font-display font-bold reedos-data " + (isVoided ? "text-slate-500 line-through" : "text-slate-900")}>{formatINR(p.amount, invoice?.currency)}</span>
                                                {isVoided ? (
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-700 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide"
                                                        title={isPrimaryAdmin
                                                            ? `Voided on ${formatDate(p.voided_at)} (${p.void_reason || "—"})${p.void_note ? ": " + p.void_note : ""}`
                                                            : "This payment has been voided. Contact a Primary Admin for the audit trail."}
                                                        data-testid={`payment-voided-chip-${p.id}`}
                                                    >
                                                        <Prohibit size={10} weight="fill" /> Voided
                                                    </span>
                                                ) : (
                                                    <StatusBadge status="Paid" />
                                                )}
                                                <span className="text-xs text-slate-500">{formatDate(p.payment_date)}</span>
                                                {p.lifecycle_state === "Corrected" && !isVoided && (
                                                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-px text-[10px] font-semibold" title="This settlement was data-entry corrected via Iter 60 'Correct' flow">
                                                        <Pencil size={9} weight="fill" /> Corrected
                                                    </span>
                                                )}
                                                {/* Iter 80.3 — MIGRATION CORRECTION badge surfaces directly
                                                    from the row's `migration_mode_corrected_at` flag, so
                                                    operators can identify migration-era corrections without
                                                    opening the audit log. Persists forever — even after
                                                    Migration Mode sign-off — for forensic traceability. */}
                                                {p.migration_mode_corrected && (
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded bg-rose-50 border border-rose-200 text-rose-800 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide"
                                                        title="This row was corrected during the tenant's Migration Mode window — see Audit Logs for the full forensic envelope"
                                                        data-testid={`payment-mcm-chip-${p.id}`}
                                                    >
                                                        <Database size={9} weight="fill" /> Migration Correction
                                                    </span>
                                                )}
                                                {(p.tds_amount || 0) > 0 && (
                                                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-px text-[10px] font-semibold" title="TDS deducted (tax credit — excluded from cash inflow)">
                                                        TDS {formatINR(p.tds_amount, invoice?.currency)} · {p.tds_status || "Deducted"}
                                                    </span>
                                                )}
                                                {p.is_cross_currency && p.receipt_currency && (
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 px-1.5 py-px text-[10px] font-semibold"
                                                        title={`Actual receipt: ${p.receipt_currency} ${Number(p.receipt_amount || 0).toLocaleString("en-IN")} · invoice settled in ${invoice?.currency || "INR"} equivalent (no FX gain/loss tracked)`}
                                                        data-testid={`payment-receipt-chip-${p.id}`}
                                                    >
                                                        {p.receipt_currency} {Number(p.receipt_amount || 0).toLocaleString("en-IN")} received
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-3">
                                                <span className="inline-flex items-center gap-1"><Hash size={11} /> {p.payment_mode}</span>
                                                {p.reference_number && <span className="inline-flex items-center gap-1 font-mono">{p.reference_number}</span>}
                                                {p.remarks && <span className="inline-flex items-center gap-1"><ChatText size={11} /> {p.remarks}</span>}
                                                {p.adjusted_at && !isVoided && (
                                                    <span className="inline-flex items-center gap-1 text-amber-700" title={`Adjusted on ${formatDate(p.adjusted_at)}${p.adjustment_note ? ": " + p.adjustment_note : ""}`}>
                                                        <Pencil size={11} weight="fill" /> Adjusted
                                                    </span>
                                                )}
                                                {p.payment_date_edited_at && !isVoided && (
                                                    <span className="inline-flex items-center gap-1 text-amber-700" title={`Date edited on ${formatDate(p.payment_date_edited_at)}`} data-testid={`payment-date-edited-chip-${p.id}`}>
                                                        <CalendarBlank size={11} weight="fill" /> Date edited
                                                    </span>
                                                )}
                                            </div>
                                            {isVoided && isPrimaryAdmin && (
                                                <div className="text-[10px] text-rose-600 mt-1 font-mono leading-tight" data-testid={`payment-void-meta-${p.id}`}>
                                                    Voided {formatDate(p.voided_at)} · {p.void_reason}
                                                    {p.void_note && <> · {p.void_note}</>}
                                                    {p.void_bank_reference && <> · ref: {p.void_bank_reference}</>}
                                                </div>
                                            )}
                                        </div>
                                        <RoleGuard permission="payment:write">
                                            {(() => {
                                                const hasTds = (p.tds_amount || 0) > 0;
                                                if (isVoided) {
                                                    // Voided rows are immutable — no action affordances.
                                                    return null;
                                                }
                                                return (
                                                    <div className="shrink-0 inline-flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setAdjustPayment(p)}
                                                            className="h-7 px-2 text-[10px] font-semibold rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors inline-flex items-center gap-1"
                                                            data-testid={`adjust-payment-button-${p.id}`}
                                                        >
                                                            <Pencil size={10} /> {hasTds ? "Adjust TDS" : "Attach TDS"}
                                                        </button>
                                                        {/* Iter 80 — Primary-Admin-only edit-date affordance. */}
                                                        {isPrimaryAdmin && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditDatePayment(p)}
                                                                className="h-7 px-2 text-[10px] font-semibold rounded border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:border-amber-300 transition-colors inline-flex items-center gap-1"
                                                                data-testid={`edit-payment-date-button-${p.id}`}
                                                                title="Edit payment date (Primary Admin only)"
                                                            >
                                                                <CalendarBlank size={10} weight="fill" /> Edit date
                                                            </button>
                                                        )}
                                                        {isPrimaryAdmin && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setCorrectPayment(p)}
                                                                className="h-7 px-2 text-[10px] font-semibold rounded border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-300 transition-colors inline-flex items-center gap-1"
                                                                data-testid={`correct-settlement-button-${p.id}`}
                                                                title="Correct a data-entry mistake on this settlement (Primary Admin only)"
                                                            >
                                                                <LockKey size={10} weight="fill" /> Correct
                                                            </button>
                                                        )}
                                                        {/* Iter 80 — Void inside a "More" menu (per
                                                            operator directive 2026-02-12 — keep Void out
                                                            of the inline button row, it's a higher-impact
                                                            action). */}
                                                        {isPrimaryAdmin && (
                                                            <div className="relative">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setOpenRowMenu(openRowMenu === p.id ? null : p.id)}
                                                                    className="h-7 w-7 rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors inline-flex items-center justify-center"
                                                                    data-testid={`payment-row-more-button-${p.id}`}
                                                                    title="More actions"
                                                                >
                                                                    <DotsThreeVertical size={14} weight="bold" />
                                                                </button>
                                                                {openRowMenu === p.id && (
                                                                    <>
                                                                        <div className="fixed inset-0 z-40" onClick={() => setOpenRowMenu(null)} />
                                                                        <div className="absolute right-0 mt-1 w-44 rounded-md border border-slate-200 bg-white shadow-lg z-50 py-1" data-testid={`payment-row-more-menu-${p.id}`}>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { setOpenRowMenu(null); setVoidPayment(p); }}
                                                                                className="w-full text-left px-3 py-2 text-xs text-rose-700 hover:bg-rose-50 inline-flex items-center gap-2"
                                                                                data-testid={`void-payment-button-${p.id}`}
                                                                            >
                                                                                <Prohibit size={12} weight="fill" />
                                                                                Void payment…
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </RoleGuard>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {/* Credit notes (if any) */}
                    {Array.isArray(invoice.credit_notes) && invoice.credit_notes.length > 0 && (
                        <div className="reedos-card" data-testid="invoice-cn-section">
                            <div className="px-5 py-4 border-b border-slate-200">
                                <div className="reedos-eyebrow">Credit notes</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                                    {invoice.credit_notes.length} issued · total {formatINR(invoice.credit_note_total, invoice?.currency)}
                                </h3>
                            </div>
                            <ul className="divide-y divide-slate-100">
                                {invoice.credit_notes.map((cn) => (
                                    <li key={cn.id} className="px-5 py-3 flex items-center justify-between" data-testid={`invoice-cn-link-${cn.id}`}>
                                        <div>
                                            <Link to={`/credit-notes/${cn.id}`} className="font-mono font-bold text-sm text-slate-900 hover:underline">
                                                {cn.cn_number}
                                            </Link>
                                            <div className="text-xs text-slate-500 mt-0.5">{formatDate(cn.cn_date)} · {cn.reason_code?.replace(/_/g, " ")}</div>
                                        </div>
                                        <div className="reedos-data text-sm font-semibold text-rose-700">- {formatINR(cn.total_amount, invoice?.currency)}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <div className="space-y-6">
                    {company && (
                        <div className="reedos-card" data-testid="invoice-detail-bill-from">
                            <div className="px-5 py-4 border-b border-slate-200">
                                <div className="reedos-eyebrow text-indigo-700">Bill from</div>
                                <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{company.legal_name}</h3>
                            </div>
                            <div className="p-5 space-y-2 text-sm">
                                {(company.registered_office || company.address) && (
                                    <div className="text-slate-600 text-xs leading-relaxed" data-testid="bill-from-address">
                                        {company.registered_office || company.address}
                                    </div>
                                )}
                                <div className="text-xs text-slate-500 space-y-0.5 pt-1">
                                    {company.gstin && (
                                        <div><span className="reedos-eyebrow mr-1.5">GSTIN</span><span className="font-mono text-slate-700">{company.gstin}</span></div>
                                    )}
                                    {company.pan && (
                                        <div><span className="reedos-eyebrow mr-1.5">PAN</span><span className="font-mono text-slate-700">{company.pan}</span></div>
                                    )}
                                    {company.state_code && (
                                        <div><span className="reedos-eyebrow mr-1.5">State</span><span className="font-mono text-slate-700">{company.state_code}</span></div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="reedos-card">
                        <div className="px-5 py-4 border-b border-slate-200">
                            <div className="reedos-eyebrow">Bill to</div>
                            <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">{invoice.client?.name}</h3>
                        </div>
                        <div className="p-5 space-y-2 text-sm">
                            {invoice.client?.gstin && <div className="font-mono text-xs">{invoice.client.gstin}</div>}
                            {invoice.client?.address && <div className="text-slate-600 text-xs">{invoice.client.address}</div>}
                            {invoice.client?.contact_name && <div className="text-slate-700">{invoice.client.contact_name}</div>}
                            <div className="text-xs text-slate-500 space-y-0.5">
                                {invoice.client?.contact_email && <div>{invoice.client.contact_email}</div>}
                                {invoice.client?.contact_phone && <div className="font-mono">{invoice.client.contact_phone}</div>}
                            </div>
                        </div>
                    </div>

                    <TimelinePanel events={invoice.events} />
                    <DocumentSection invoice={invoice} onUploaded={load} />
                </div>
            </div>
            <AdjustPaymentDialog
                payment={adjustPayment}
                invoice={invoice}
                onClose={() => setAdjustPayment(null)}
                onAdjusted={() => { setAdjustPayment(null); load(); }}
                currency={invoice?.currency}
            />
            <CorrectSettlementDialog
                open={!!correctPayment}
                onOpenChange={(v) => !v && setCorrectPayment(null)}
                payment={correctPayment}
                invoice={invoice}
                onDone={() => { setCorrectPayment(null); load(); }}
            />
            <VoidPaymentDialog
                open={!!voidPayment}
                onOpenChange={(v) => !v && setVoidPayment(null)}
                payment={voidPayment}
                onDone={() => { setVoidPayment(null); load(); }}
            />
            <EditPaymentDateDialog
                open={!!editDatePayment}
                onOpenChange={(v) => !v && setEditDatePayment(null)}
                payment={editDatePayment}
                onDone={() => { setEditDatePayment(null); load(); }}
            />
            <EditInvoiceDateDialog
                open={editDateOpen}
                onOpenChange={setEditDateOpen}
                invoice={invoice}
                onDone={() => { setEditDateOpen(false); load(); }}
            />
        </div>
    );
}

function AdjustPaymentDialog({ payment, invoice, onClose, onAdjusted, currency }) {
    const [form, setForm] = useState({
        tds_amount: "", tds_percentage: "", tds_status: "", note: "",
    });
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef("");

    useEffect(() => {
        if (payment) {
            const next = {
                tds_amount: payment.tds_amount ?? 0,
                tds_percentage: payment.tds_percentage ?? 0,
                tds_status: payment.tds_status || "Deducted",
                note: "",
            };
            setForm(next);
            baselineRef.current = JSON.stringify(next);
        }
    }, [payment]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    // ── Iter 55 — Reconciliation preview ─────────────────────────────
    // Goal: make the "Adjust TDS" dialog feel like "I'm attaching TDS
    // to THIS existing settlement". Operators were occasionally clicking
    // "Record settlement" again to record TDS, creating a confusing
    // second payment row. The reconciliation banner mirrors the
    // economic identity used in the Record-settlement dialog
    // (Cash + TDS = Gross Invoice) and shows the *post-save* settlement
    // state up-front, so it is obvious that this action *settles* the
    // invoice rather than creating a separate event.
    const grossInvoice = Number(invoice?.total_amount || 0);
    const billingAmount = Number(invoice?.subtotal || 0);
    const cashOnThisSettlement = Number(payment?.amount || 0);
    const currentTds = Number(payment?.tds_amount || 0);
    const previewTds = Number(form.tds_amount) || 0;
    const tdsDelta = round2(previewTds - currentTds);
    const previewTdsPctRaw = Number(form.tds_percentage) || 0;
    // Live "what other settlements contributed" (cash + TDS across
    // ALL OTHER payment rows on this invoice). Used so the status
    // preview reflects the WHOLE-INVOICE settlement state after save,
    // not just this row in isolation.
    // Iter 80 — voided rows are reversed; exclude them so the preview
    // matches the server's post-save status calculation exactly.
    const otherCash = Array.isArray(invoice?.payments)
        ? invoice.payments.filter((p) => p.id !== payment?.id && !p.voided_at).reduce((s, p) => s + Number(p.amount || 0), 0)
        : 0;
    const otherTds = Array.isArray(invoice?.payments)
        ? invoice.payments.filter((p) => p.id !== payment?.id && !p.voided_at).reduce((s, p) => s + Number(p.tds_amount || 0), 0)
        : 0;
    const creditNoteTotal = Number(invoice?.credit_note_total || 0);
    // Whole-invoice tallies after the save.
    const totalCashAfter = round2(otherCash + cashOnThisSettlement);
    const totalTdsAfter = round2(otherTds + previewTds);
    const settledAfter = round2(totalCashAfter + totalTdsAfter + creditNoteTotal);
    const outstandingAfter = round2(grossInvoice - settledAfter);
    // For "Cash + TDS = Gross Invoice" identity on THIS settlement row
    // (the row-local reconciliation the user explicitly called out):
    const rowSettlementGross = round2(cashOnThisSettlement + previewTds);
    // Validation: cannot over-settle the invoice.
    const overSettleBy = round2(-outstandingAfter);
    const willOverSettle = overSettleBy > 0.01;
    // Status preview — economic settlement state language, not just numbers.
    // Three buckets:
    //   1. Fully Settled  — settledAfter ≥ gross (within 1 paisa)
    //   2. Partial         — 0 < settledAfter < gross
    //   3. Over-settled    — settledAfter > gross (blocking)
    let statusPreview;
    if (willOverSettle) {
        statusPreview = {
            kind: "rose",
            label: "Cannot save — would over-settle the invoice",
            detail: `Reduce TDS by ${formatINR(overSettleBy, currency)} to balance.`,
        };
    } else if (outstandingAfter <= 0.01) {
        statusPreview = {
            kind: "emerald",
            label: "Invoice will become Fully Settled",
            detail: `Cash + TDS + credits = ${formatINR(grossInvoice, currency)} (Gross invoice)`,
        };
    } else {
        statusPreview = {
            kind: "amber",
            label: "Invoice will remain Partial",
            detail: `Outstanding after save: ${formatINR(outstandingAfter, currency)}`,
        };
    }
    const STATUS_PALETTE = {
        emerald: { card: "border-emerald-300 bg-emerald-50", label: "text-emerald-900", detail: "text-emerald-800" },
        amber:   { card: "border-amber-300 bg-amber-50",   label: "text-amber-900",   detail: "text-amber-800" },
        rose:    { card: "border-rose-300 bg-rose-50",     label: "text-rose-900",    detail: "text-rose-800" },
    };

    // Iter 55 — Convenience: clicking the "Apply" chip on a default
    // 10% (194J) TDS sets both TDS Amount and TDS % to the standard
    // value and skips the manual math.
    const apply194J = () => {
        const pct = 10;
        const tdsFromBilling = round2(billingAmount * pct / 100);
        setForm((f) => ({ ...f, tds_percentage: pct, tds_amount: tdsFromBilling }));
    };
    // When operator edits the % field, recompute TDS Amount from billing.
    const onPctChange = (val) => {
        const pct = Number(val) || 0;
        const tdsFromBilling = round2(billingAmount * pct / 100);
        setForm((f) => ({ ...f, tds_percentage: pct, tds_amount: tdsFromBilling }));
    };
    // When operator edits TDS Amount directly, leave % alone (it
    // becomes "custom" relative to %, just like the Record-settlement
    // dialog does).
    const onAmountChange = (val) => {
        setForm((f) => ({ ...f, tds_amount: Number(val) || 0 }));
    };

    const submit = async () => {
        if (!payment) return;
        if (willOverSettle) {
            toast.error(`Would over-settle the invoice by ${formatINR(overSettleBy, currency)}`);
            return;
        }
        const body = { note: form.note || undefined };
        if (String(form.tds_amount) !== String(payment.tds_amount ?? 0)) {
            body.tds_amount = Number(form.tds_amount);
        }
        if (String(form.tds_percentage) !== String(payment.tds_percentage ?? 0)) {
            body.tds_percentage = Number(form.tds_percentage);
        }
        if (form.tds_status !== payment.tds_status) {
            body.tds_status = form.tds_status;
        }
        if (body.tds_amount === undefined && !body.tds_status && body.tds_percentage === undefined) {
            toast.error("No changes to adjust");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/billing/payment/${payment.id}/adjust`, body);
            const ok = outstandingAfter <= 0.01
                ? `Invoice fully settled · Cash ${formatINR(totalCashAfter, currency)} + TDS ${formatINR(totalTdsAfter, currency)} = ${formatINR(grossInvoice, currency)}`
                : `TDS ${formatINR(previewTds, currency)} attached · ${formatINR(outstandingAfter, currency)} outstanding`;
            toast.success(ok);
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            onAdjusted();
        } catch (err) {
            toast.error(formatApiError(err, "Adjustment failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!payment) return null;
    const isAttachMode = currentTds <= 0.01;
    const palette = STATUS_PALETTE[statusPreview.kind];
    return (
        <FormDialog
            open={!!payment}
            onOpenChange={(o) => { if (!o) onClose(); }}
            isDirty={isDirty}
            contentClassName="sm:max-w-lg"
            testId="adjust-payment-dialog"
            title={
                <span className="flex items-center gap-2">
                    <Pencil size={18} weight="duotone" className="text-amber-600" />
                    {isAttachMode ? "Attach TDS" : "Adjust TDS"} — same settlement
                </span>
            }
            description={
                isAttachMode
                    ? "Attaches a TDS deduction to the existing settlement. No new settlement row is created — the same row is augmented and the invoice status auto-recomputes."
                    : `Edits TDS on settlement ${payment.id}. The principal cash (${formatINR(payment.amount, currency)}) is locked for audit integrity.`
            }
        >
            <div className="space-y-3">
                {/* ── Iter 55 — Settlement reconciliation card ───────── */}
                <div
                    className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-3 space-y-2"
                    data-testid="attach-tds-reconciliation"
                >
                    <div className="reedos-eyebrow text-[10px] text-indigo-800 flex items-baseline justify-between">
                        <span>Settlement reconciliation</span>
                        <span className="text-[9px] font-normal italic text-indigo-700/70 normal-case">
                            Cash + TDS = Gross invoice (this settlement)
                        </span>
                    </div>
                    <dl className="text-[12px] space-y-1">
                        <FormulaRow
                            label="Gross invoice"
                            value={formatINR(grossInvoice, currency)}
                            bold
                            testid="attach-tds-gross-invoice"
                        />
                        <FormulaRow
                            label="Cash already received (this settlement)"
                            value={formatINR(cashOnThisSettlement, currency)}
                            tone="emerald"
                            testid="attach-tds-cash"
                        />

                        {/* Editable TDS % + Amount inline (mirrors Record-settlement
                            formula card so operators land in a familiar layout). */}
                        <div className="flex items-center justify-between pt-1" data-testid="attach-tds-edit-row">
                            <dt className="text-[11px] font-semibold text-amber-900 flex items-center gap-2">
                                TDS
                                <span className="inline-flex items-center gap-1">
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="30"
                                        value={form.tds_percentage}
                                        onChange={(e) => onPctChange(e.target.value)}
                                        className="h-6 w-14 px-1.5 text-[11px] font-mono text-center bg-white border-amber-200"
                                        data-testid="adjust-tds-percentage"
                                    />
                                    <span className="text-amber-700">%</span>
                                </span>
                                {isAttachMode && (
                                    <button
                                        type="button"
                                        onClick={apply194J}
                                        className="text-[9px] font-semibold uppercase text-indigo-700 hover:text-indigo-900 bg-white border border-indigo-200 rounded px-1.5 py-0.5"
                                        data-testid="attach-tds-apply-194j"
                                        title="Apply standard 194J rate (10% on billing amount)"
                                    >
                                        Apply 10% (194J)
                                    </button>
                                )}
                            </dt>
                            <dd className="flex items-center gap-1">
                                <span className="text-amber-700 text-[11px]">+</span>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={form.tds_amount}
                                    onChange={(e) => onAmountChange(e.target.value)}
                                    className="h-7 w-32 px-2 text-[12px] font-mono text-right bg-white border-amber-200"
                                    data-testid="adjust-tds-amount"
                                />
                            </dd>
                        </div>

                        <FormulaRow
                            label="Gross settlement (this row)"
                            value={formatINR(rowSettlementGross, currency)}
                            tone="indigo"
                            bold
                            divider
                            testid="attach-tds-row-gross"
                        />
                    </dl>
                </div>

                {/* ── Iter 55 — Status preview tile ───────────────────── */}
                <div
                    className={`rounded-md border px-3 py-2.5 ${palette.card}`}
                    data-testid="attach-tds-status-preview"
                >
                    <div className={`text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5 ${palette.label}`}>
                        {statusPreview.kind === "emerald" && <span>✓</span>}
                        {statusPreview.kind === "amber" && <span>◐</span>}
                        {statusPreview.kind === "rose" && <span>!</span>}
                        After save → {statusPreview.label}
                    </div>
                    <div className={`text-[11px] mt-0.5 ${palette.detail}`}>{statusPreview.detail}</div>
                </div>

                {/* ── TDS lifecycle stage ─────────────────────────────── */}
                <div>
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">TDS lifecycle stage</Label>
                    <select value={form.tds_status} onChange={(e) => setForm({ ...form, tds_status: e.target.value })} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="adjust-tds-status">
                        {["Deducted", "Received", "Verified", "Claimed", "Adjusted"].map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <Label className="text-[10px] font-semibold text-slate-500 uppercase">Reconciliation note (optional)</Label>
                    <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. TDS @ 10% (194J) attached against Form 16A Q1 FY26-27" className="mt-1 h-9 text-sm" data-testid="adjust-note" />
                </div>
            </div>
            <DialogFooter>
                <FormDialogCancel variant="ghost" testId="adjust-cancel">Cancel</FormDialogCancel>
                <Button
                    onClick={submit}
                    disabled={busy || willOverSettle || tdsDelta === 0 && form.tds_status === payment.tds_status}
                    data-testid="adjust-submit"
                    className="gap-1.5 bg-amber-600 hover:bg-amber-700"
                >
                    <Pencil size={13} /> {busy ? "Saving…" : (isAttachMode ? "Attach TDS & settle" : "Save adjustment")}
                </Button>
            </DialogFooter>
        </FormDialog>
    );
}
